import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin, getAdminToken } from '@/lib/api';
import { useForceLightTheme } from '@/lib/theme';

export default function AdminLogin() {
  useForceLightTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Admin — OpenMeta';
    if (getAdminToken()) {
      navigate('/admin', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    if (!username.trim()) return setError("Merci d'indiquer votre identifiant.");
    if (!password) return setError("Merci d'indiquer votre mot de passe.");
    setLoading(true);
    const res = await adminLogin({ username: username.trim(), password });
    setLoading(false);
    if (res.ok) {
      navigate('/admin', { replace: true });
    } else {
      setError(res.message || 'Identifiants incorrects.');
    }
  };

  return (
    <div className="min-h-screen bg-[var(--om-bg)]" data-testid="page-admin-login">
      <div className="om-container flex min-h-screen items-center justify-center py-16">
        <div className="relative w-full max-w-md">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-6 -z-10 rounded-[36px] opacity-70 blur-3xl"
            style={{ background: 'radial-gradient(closest-side, rgba(139,92,246,0.24), rgba(16,185,129,0.14), transparent 70%)' }}
          />
          <div className="rounded-[28px] border border-[var(--om-border)] bg-white p-8 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.3)] md:p-10">
            <div className="mb-6 flex items-center gap-3">
              <img
                src="/brand/logo-mark.png"
                alt="OpenMeta"
                className="h-11 w-11"
                width="44"
                height="44"
                data-testid="admin-login-logo"
              />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                  Espace protégé
                </div>
                <div className="text-lg font-bold text-[var(--om-ink)]">OpenMeta Admin</div>
              </div>
            </div>

            <form onSubmit={handleSubmit} noValidate data-testid="admin-login-form" className="space-y-4">
              <div>
                <label htmlFor="admin-username" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                  Identifiant
                </label>
                <input
                  id="admin-username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); if (error) setError(''); }}
                  data-testid="admin-username-input"
                  className="om-input"
                  placeholder="admin"
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                  Mot de passe
                </label>
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (error) setError(''); }}
                  data-testid="admin-password-input"
                  className="om-input"
                  placeholder="••••••••"
                />
              </div>
              {error && (
                <div data-testid="admin-login-error" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="admin-login-submit"
                className="om-btn-primary w-full"
              >
                {loading ? 'Connexion…' : 'Se connecter'}
              </button>
            </form>

            <div className="mt-6 text-center text-xs text-[var(--om-muted-2)]">
              Cet espace est réservé à l'équipe OpenMeta.
            </div>
            <div className="mt-3 text-center">
              <a
                href="/"
                className="text-xs font-medium text-[var(--om-muted)] hover:text-[var(--om-ink)] transition-colors"
                data-testid="admin-login-back-home"
              >
                ← Retour au site
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
